import { useMemo } from "react";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import {
  STATUS_LIST,
  KanbanDefaultStates,
} from "../../ProjectList/CanvasKanban";
import { Status } from "@/types/status";
import { STATUS_TYPE_MAPPING } from "../constants";
import { useActiveProject } from "@/utils/utils";
import { getColumnConfig } from "../utils/columnConfigUtils";

interface CanvasStatusManagementHook {
  comprehensiveStatusList: Status[];
  allProjectStatuses: Array<{
    key: string;
    status: string;
    isDefault: boolean;
    type: string;
  }>;
  getAllStatusOptions: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  customProjectStatuses: ProjectStatus[];
}

export const buildComprehensiveStatusList = (
  customProjectStatuses: ProjectStatus[],
): Status[] => {
  const columnConfig = getColumnConfig(customProjectStatuses);
  const result: Status[] = [];

  columnConfig.forEach((config) => {
    let statusObj;

    if (config.isDefault) {
      statusObj = STATUS_LIST.find(
        (s) => s.name === config.name || s.id === config.type,
      );
    } else {
      statusObj = STATUS_LIST.find((s) => s.id === config.type);

      if (!statusObj) {
        const mappedType =
          STATUS_TYPE_MAPPING[config.type as keyof typeof STATUS_TYPE_MAPPING];
        if (mappedType) {
          statusObj = STATUS_LIST.find((s) => s.id === mappedType);
        }
      }
    }

    if (!statusObj) {
      statusObj = STATUS_LIST.find((s) => s.id === "backlog");
    }

    if (!statusObj) return;

    const statusType = config.type;

    result.push({
      id: config.isDefault ? statusObj.id : config.key,
      name: config.name,
      icon: statusObj.icon,
      badgeIcon: statusObj.badgeIcon,
      filterIcon: statusObj.filterIcon,
      type: statusType,
    });
  });

  return result;
};

export const useCanvasStatusManagement = (): CanvasStatusManagementHook => {
  const currentProject = useActiveProject();

  const customProjectStatusesMemo = useMemo(() => {
    if (!currentProject?.statuses || !Array.isArray(currentProject.statuses))
      return [];
    return currentProject.statuses;
  }, [currentProject?.statuses]);

  const comprehensiveStatusList = useMemo(() => {
    return buildComprehensiveStatusList(customProjectStatusesMemo);
  }, [customProjectStatusesMemo]);

  const allProjectStatuses = useMemo(() => {
    const columnConfig = getColumnConfig(customProjectStatusesMemo);

    const sortedStatuses: Array<{
      key: string;
      status: string;
      isDefault: boolean;
      type: string;
    }> = [];

    columnConfig.forEach((config) => {
      sortedStatuses.push({
        key: config.key,
        status: config.name,
        isDefault: config.isDefault,
        type: config.type,
      });
    });

    return sortedStatuses;
  }, [customProjectStatusesMemo]);

  const getAllStatusOptions = useMemo(() => {
    const projectStatuses = comprehensiveStatusList.map((status) => ({
      id: status.id,
      name: status.name,
      type: status.id,
    }));

    return projectStatuses;
  }, [comprehensiveStatusList]);

  return {
    comprehensiveStatusList,
    allProjectStatuses,
    getAllStatusOptions,
    customProjectStatuses: customProjectStatusesMemo,
  };
};
